import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ExceptionsTable } from '@/components/app-exceptions/exceptions-table'
import type { AppException } from '@/types/app-exceptions'

interface ErrorsPaneProps {
  projectSlug: string
  conversationId?: number
}

export function ErrorsPane({ projectSlug, conversationId }: ErrorsPaneProps) {
  const { t } = useTranslation('conversations')
  const [exceptions, setExceptions] = useState<AppException[]>([])
  const [statusFilter, setStatusFilter] = useState('unresolved')
  const [loading, setLoading] = useState(true)

  const fetchExceptions = useCallback(async (status: string) => {
    setLoading(true)
    try {
      const response = await fetch(`/projects/${projectSlug}/errors.json?status=${status}`, {
        headers: { 'Accept': 'application/json' },
      })
      if (response.ok) {
        const data = await response.json()
        setExceptions(data.exceptions || [])
      }
    } catch {
      // Silently handle fetch errors
    } finally {
      setLoading(false)
    }
  }, [projectSlug])

  useEffect(() => {
    fetchExceptions(statusFilter)
  }, [fetchExceptions, statusFilter])

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status)
  }

  const handleResolve = async (id: number) => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const response = await fetch(`/projects/${projectSlug}/errors/${id}/resolve`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
    })
    if (response.ok) {
      toast.success(t('errors.toasts.resolved'))
      await fetchExceptions(statusFilter)
    } else {
      toast.error(t('errors.toasts.resolveFailed'))
    }
  }

  const handleIgnore = async (id: number) => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const response = await fetch(`/projects/${projectSlug}/errors/${id}/ignore`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
    })
    if (response.ok) {
      toast.success(t('errors.toasts.ignored'))
      await fetchExceptions(statusFilter)
    } else {
      toast.error(t('errors.toasts.ignoreFailed'))
    }
  }

  const handleAnalyzeAndFix = async (id: number) => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const response = await fetch(`/projects/${projectSlug}/errors/${id}/analyze_and_fix`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
      body: JSON.stringify({ conversation_id: conversationId }),
    })
    if (response.ok) {
      toast.success(t('errors.toasts.analyzeAndFixSuccess'))
      await fetchExceptions(statusFilter)
    } else {
      toast.error(t('errors.toasts.analyzeAndFixFailed'))
    }
  }

  return (
    <div className="p-4">
      <h3 className="text-lg font-semibold mb-4">{t('errors.title')}</h3>
      <ExceptionsTable
        exceptions={exceptions}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        onResolve={handleResolve}
        onIgnore={handleIgnore}
        onAnalyzeAndFix={handleAnalyzeAndFix}
        loading={loading}
      />
    </div>
  )
}
